import { useEffect, useState } from 'react';
import { isAxiosError } from 'axios';
import { useMutation } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@werkstatt/ui';
import { Button } from '@werkstatt/ui';
import { Input } from '@werkstatt/ui';
import { Label } from '@werkstatt/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@werkstatt/ui';
import { Textarea } from '@werkstatt/ui';
import { useToast } from '../../hooks/use-toast';
import api from '../../lib/api';
import type { Customer, CustomerCreate, CustomerUpdate } from '../../types/customer';

interface CustomerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customer: Customer | null;
  onSuccess: () => void;
}

export function CustomerDialog({
  open,
  onOpenChange,
  customer,
  onSuccess,
}: CustomerDialogProps) {
  const { toast } = useToast();
  const isEdit = !!customer;

  const [formData, setFormData] = useState<Partial<CustomerCreate>>({
    customer_number: '',
    salutation: '',
    first_name: '',
    last_name: '',
    company_name: '',
    email: '',
    phone: '',
    mobile: '',
    street: '',
    postal_code: '',
    city: '',
    country: 'DE',
    license_number: '',
    license_class: '',
    is_active: true,
    is_blacklisted: false,
  });

  // Reset form when dialog opens/closes or customer changes
  useEffect(() => {
    if (customer) {
      setFormData({
        customer_number: customer.customer_number || '',
        salutation: customer.salutation || '',
        first_name: customer.first_name || '',
        last_name: customer.last_name || '',
        company_name: customer.company_name || '',
        email: customer.email || '',
        phone: customer.phone || '',
        mobile: customer.mobile || '',
        street: customer.street || '',
        postal_code: customer.postal_code || '',
        city: customer.city || '',
        country: customer.country || 'DE',
        license_number: customer.license_number || '',
        license_issued_at: customer.license_issued_at || undefined,
        license_expires_at: customer.license_expires_at || undefined,
        license_class: customer.license_class || '',
        date_of_birth: customer.date_of_birth || undefined,
        notes: customer.notes || '',
        is_active: customer.is_active,
        is_blacklisted: customer.is_blacklisted,
        blacklist_reason: customer.blacklist_reason || '',
      });
    } else {
      setFormData({
        customer_number: '',
        salutation: '',
        first_name: '',
        last_name: '',
        company_name: '',
        email: '',
        phone: '',
        mobile: '',
        street: '',
        postal_code: '',
        city: '',
        country: 'DE',
        license_number: '',
        license_class: '',
        is_active: true,
        is_blacklisted: false,
      });
    }
  }, [customer, open]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: CustomerCreate) => {
      const response = await api.post('/customers', data);
      return response.data;
    },
    onSuccess: () => {
      toast({
        title: 'Kunde erstellt',
        description: 'Der Kunde wurde erfolgreich erstellt.',
      });
      onSuccess();
    },
    onError: (error) => {
      toast({
        title: 'Fehler',
        description:
          (isAxiosError(error) ? error.response?.data?.detail : undefined) ||
          'Der Kunde konnte nicht erstellt werden.',
        variant: 'destructive',
      });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: CustomerUpdate) => {
      const response = await api.put(`/customers/${customer!.id}`, data);
      return response.data;
    },
    onSuccess: () => {
      toast({
        title: 'Kunde aktualisiert',
        description: 'Der Kunde wurde erfolgreich aktualisiert.',
      });
      onSuccess();
    },
    onError: (error) => {
      toast({
        title: 'Fehler',
        description:
          (isAxiosError(error) ? error.response?.data?.detail : undefined) ||
          'Der Kunde konnte nicht aktualisiert werden.',
        variant: 'destructive',
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (isEdit) {
      updateMutation.mutate(formData as CustomerUpdate);
    } else {
      createMutation.mutate(formData as CustomerCreate);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">
            {isEdit ? 'Kunde bearbeiten' : 'Neuer Kunde'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal Data */}
          <div className="space-y-4">
            <h3 className="font-semibold">Persönliche Daten</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="customer_number">Kundennummer</Label>
                <Input
                  id="customer_number"
                  value={formData.customer_number}
                  onChange={(e) =>
                    setFormData({ ...formData, customer_number: e.target.value })
                  }
                  placeholder="z.B. KD-001"
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="company_name">Firmenname</Label>
                <Input
                  id="company_name"
                  value={formData.company_name}
                  onChange={(e) =>
                    setFormData({ ...formData, company_name: e.target.value })
                  }
                  placeholder="Optional für Firmenkunden"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="salutation">Anrede</Label>
                <Select
                  value={formData.salutation}
                  onValueChange={(value: string) =>
                    setFormData({ ...formData, salutation: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Herr">Herr</SelectItem>
                    <SelectItem value="Frau">Frau</SelectItem>
                    <SelectItem value="Firma">Firma</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="first_name">Vorname</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) =>
                    setFormData({ ...formData, first_name: e.target.value })
                  }
                />
              </div>
              <div>
                <Label htmlFor="last_name">Nachname</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) =>
                    setFormData({ ...formData, last_name: e.target.value })
                  }
                />
              </div>
            </div>

            <div>
              <Label htmlFor="date_of_birth">Geburtsdatum</Label>
              <Input
                id="date_of_birth"
                type="date"
                value={formData.date_of_birth || ''}
                onChange={(e) =>
                  setFormData({ ...formData, date_of_birth: e.target.value })
                }
              />
            </div>
          </div>

          {/* Contact */}
          <div className="space-y-4">
            <h3 className="font-semibold">Kontaktdaten</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="email">E-Mail</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                />
              </div>
              <div>
                <Label htmlFor="phone">Telefon</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                />
              </div>
              <div>
                <Label htmlFor="mobile">Mobil</Label>
                <Input
                  id="mobile"
                  value={formData.mobile}
                  onChange={(e) =>
                    setFormData({ ...formData, mobile: e.target.value })
                  }
                />
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="space-y-4">
            <h3 className="font-semibold">Adresse</h3>
            <div>
              <Label htmlFor="street">Straße und Hausnummer</Label>
              <Input
                id="street"
                value={formData.street}
                onChange={(e) =>
                  setFormData({ ...formData, street: e.target.value })
                }
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="postal_code">PLZ</Label>
                <Input
                  id="postal_code"
                  value={formData.postal_code}
                  onChange={(e) =>
                    setFormData({ ...formData, postal_code: e.target.value })
                  }
                />
              </div>
              <div>
                <Label htmlFor="city">Stadt</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) =>
                    setFormData({ ...formData, city: e.target.value })
                  }
                />
              </div>
              <div>
                <Label htmlFor="country">Land</Label>
                <Input
                  id="country"
                  value={formData.country}
                  onChange={(e) =>
                    setFormData({ ...formData, country: e.target.value })
                  }
                  maxLength={2}
                  placeholder="DE"
                />
              </div>
            </div>
          </div>

          {/* Driver's License */}
          <div className="space-y-4">
            <h3 className="font-semibold">Führerschein</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="license_number">Führerscheinnummer</Label>
                <Input
                  id="license_number"
                  value={formData.license_number}
                  onChange={(e) =>
                    setFormData({ ...formData, license_number: e.target.value })
                  }
                />
              </div>
              <div>
                <Label htmlFor="license_class">Führerscheinklasse</Label>
                <Input
                  id="license_class"
                  value={formData.license_class}
                  onChange={(e) =>
                    setFormData({ ...formData, license_class: e.target.value })
                  }
                  placeholder="z.B. B, BE, C1"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="license_issued_at">Ausgestellt am</Label>
                <Input
                  id="license_issued_at"
                  type="date"
                  value={formData.license_issued_at || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, license_issued_at: e.target.value })
                  }
                />
              </div>
              <div>
                <Label htmlFor="license_expires_at">Gültig bis</Label>
                <Input
                  id="license_expires_at"
                  type="date"
                  value={formData.license_expires_at || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, license_expires_at: e.target.value })
                  }
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="notes">Notizen</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              placeholder="Optionale Notizen zum Kunden..."
              rows={3}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Abbrechen
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Wird gespeichert...' : isEdit ? 'Speichern' : 'Erstellen'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
